import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback,useEffect, useMemo, useRef } from 'react';

import { OEM_REPORT_KEY_ENUM } from '@ekhodealer/ekho-common';
import { Button, Dropdown, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import {
	formatReportDate,
	formatReportNumber,
	formatVehiclePaymentStatus,
	OrderDisplayStatus,
	VEHICLE_PAYMENT_STATUS_TO_DISPLAY_STATUS,
} from '@common/lib/reports';
import { PLACEHOLDER } from '@common/lib/strings';

import { usePostReports } from '../../api/usePostReports';
import {
	isGeneratingOrdersReportAtom,
	type OrderReportItem,
	ordersReportDataAtom,
} from '../../atoms/ordersAtoms';

import './styles.css';

const selectedMonthAtom = atom(String(new Date().getMonth() + 1)); // '1'-'12'
const selectedYearAtom = atom(String(new Date().getFullYear()));

const monthOptions = [
	{ id: '1', label: 'January' },
	{ id: '2', label: 'February' },
	{ id: '3', label: 'March' },
	{ id: '4', label: 'April' },
	{ id: '5', label: 'May' },
	{ id: '6', label: 'June' },
	{ id: '7', label: 'July' },
	{ id: '8', label: 'August' },
	{ id: '9', label: 'September' },
	{ id: '10', label: 'October' },
	{ id: '11', label: 'November' },
	{ id: '12', label: 'December' },
];

// Generate year options for current year and past 10 years
const getYearOptions = () => {
	const currentYear = new Date().getFullYear();
	const years = [];
	for (let i = 0; i <= 10; i++) {
		const year = currentYear - i;
		years.push({ id: String(year), label: year.toString() });
	}
	return years;
};

export default function ReportsOrdersToolbar() {
	const [selectedMonth, setSelectedMonth] = useAtom(selectedMonthAtom);
	const [selectedYear, setSelectedYear] = useAtom(selectedYearAtom);
	const setOrdersReportData = useSetAtom(ordersReportDataAtom);
	const setIsGenerating = useSetAtom(isGeneratingOrdersReportAtom);
	const isGenerating = useAtomValue(isGeneratingOrdersReportAtom);
	const reportData = useAtomValue(ordersReportDataAtom);
	const { mutateAsync: generateReportAsync } = usePostReports();

	const yearOptions = useMemo(() => getYearOptions(), []);

	const hasReportData = useMemo(() => {
		return reportData !== null && reportData.length > 0;
	}, [reportData]);

	const handleGenerateReport = useCallback(() => {
		// Calculate start and end dates for the selected month
		const year = parseInt(selectedYear, 10);
		const month = parseInt(selectedMonth, 10);
		const startDate = new Date(year, month - 1, 1);
		const endDate = new Date(year, month, 0, 23, 59, 59, 999); // Last day of month

		// Send timestamps (numbers) as strings, which the backend will parseInt
		const reportPeriodStart = String(startDate.getTime());
		const reportPeriodEnd = String(endDate.getTime());

		setIsGenerating(true);

		const params = {
			reportKey: OEM_REPORT_KEY_ENUM.SALES_BY_CUSTOMER_NAME,
			reportPeriodStart,
			reportPeriodEnd,
		};

		// Use mutateAsync to ensure we can handle the promise directly
		generateReportAsync(params)
			.then((data: unknown) => {
				try {
					// Map the API response to OrderReportItem format
					// The API returns data wrapped in { success: true, data: [...] }
					const responseData = Array.isArray(data)
						? data
						: (data as { data: unknown[] })?.data || [];
					const mappedData = responseData.map(
						(item: Record<string, unknown>): OrderReportItem => {
							// Map vehiclePaymentStatus to our status enum for display
							const vehiclePaymentStatus = String(
								item.vehiclePaymentStatus || 'not_paid',
							);
							const status =
								VEHICLE_PAYMENT_STATUS_TO_DISPLAY_STATUS[vehiclePaymentStatus] ||
								OrderDisplayStatus.NOT_PAID;

							// Helper to parse value - preserve as string if backend already formatted it
							const parseValue = (value: unknown): number | string => {
								if (value === null || value === undefined) return '0';
								if (typeof value === 'number') return value;
								if (typeof value === 'string') {
									// If it's already formatted (has decimal places), return as string
									if (value.includes('.') && value.split('.')[1]?.length === 2) {
										return value;
									}
									const parsed = parseFloat(value);
									return isNaN(parsed) ? '0' : parsed;
								}
								return '0';
							};

							// Helper to parse date (can be number or string timestamp)
							const parseDate = (value: unknown): number | string => {
								if (value === null || value === undefined) return '';
								if (typeof value === 'number') return value;
								if (typeof value === 'string') {
									const parsed = parseInt(value, 10);
									return isNaN(parsed) ? '' : parsed;
								}
								return '';
							};

							return {
								placedOrderId: String(item.placedOrderId || ''),
								uid: String(item.uid || ''),
								formattedVin: String(item.formattedVin || ''),
								customer: String(item.customer || ''),
								date: parseDate(item.date),
								deliveryDate: parseDate(item.deliveryDate),
								dispatchDate: parseDate(item.dispatchDate),
								basePrice: parseValue(item.basePrice),
								addOns: parseValue(item.addOns),
								totalDiscount: parseValue(item.totalDiscount),
								vehiclePriceNoFees: parseValue(item.vehiclePriceNoFees),
								taxes: parseValue(item.taxes),
								altGovFees: parseValue(item.altGovFees),
								deliveryFee: parseValue(item.deliveryFee),
								pickUpFee: parseValue(item.pickUpFee),
								buyerCoveredProcessingFees: parseValue(
									item.buyerCoveredProcessingFees,
								),
								totalDue: parseValue(item.totalDue),
								paidByCustomer: parseValue(item.paidByCustomer),
								paidByCustomerOffPlatform: parseValue(
									item.paidByCustomerOffPlatform,
								),
								paidByLender: parseValue(item.paidByLender),
								totalPaid: parseValue(item.totalPaid),
								invoicePrice: parseValue(item.invoicePrice),
								oemCollectedShippingFees: parseValue(item.oemCollectedShippingFees),
								grossOemOwed: parseValue(item.grossOemOwed),
								vehiclePaymentStatus: String(item.vehiclePaymentStatus || ''),
								paymentNotes: String(item.paymentNotes || ''),
								externalStatus: String(item.externalStatus || ''),
								disputeStatus: String(item.disputeStatus || PLACEHOLDER),
								vehicleFulfillmentStatus: String(
									item.vehicleFulfillmentStatus || '',
								),
								status, // Computed status for display
							};
						},
					);
					setOrdersReportData(mappedData);
					setIsGenerating(false);
				} catch (error) {
					console.error('Error mapping report data:', error);
					setOrdersReportData([]);
					setIsGenerating(false);
				}
			})
			.catch((error) => {
				console.error('Error generating report:', error);
				setIsGenerating(false);
			});
	}, [selectedYear, selectedMonth, setIsGenerating, generateReportAsync, setOrdersReportData]);

	const hasGeneratedOnLoad = useRef(false);

	// Auto-generate report on component mount with current month/year
	useEffect(() => {
		if (!hasGeneratedOnLoad.current && reportData === null) {
			hasGeneratedOnLoad.current = true;
			handleGenerateReport();
		}
	}, [reportData, handleGenerateReport]);

	const handleDownloadReport = () => {
		if (!reportData || reportData.length === 0) return;

		// Define CSV headers - matching table column order exactly
		const headers = [
			'Placed order ID',
			'ID',
			'VIN',
			'Customer',
			'Date',
			'Delivery date',
			'Dispatch date',
			'Base price',
			'Add ons',
			'Total discount',
			'Vehicle price no fees',
			'Taxes',
			'Alt gov fees',
			'Delivery fee',
			'Pick up fee',
			'Buyer covered processing fees',
			'Total due',
			'Paid by customer',
			'Paid off platform',
			'Paid by lender',
			'Total paid',
			'Invoice price',
			'Client collected shipping fees',
			'Gross client owed',
			'Vehicle payment status',
			'Payment notes',
			'Dispute status',
			'External status',
		];

		// Convert report data to CSV rows - matching table column order exactly
		const rows = reportData.map((item) => [
			item.placedOrderId,
			item.uid,
			item.formattedVin,
			item.customer,
			formatReportDate(item.date),
			formatReportDate(item.deliveryDate),
			formatReportDate(item.dispatchDate),
			formatReportNumber(item.basePrice),
			formatReportNumber(item.addOns),
			formatReportNumber(item.totalDiscount),
			formatReportNumber(item.vehiclePriceNoFees),
			formatReportNumber(item.taxes),
			formatReportNumber(item.altGovFees),
			formatReportNumber(item.deliveryFee),
			formatReportNumber(item.pickUpFee),
			formatReportNumber(item.buyerCoveredProcessingFees),
			formatReportNumber(item.totalDue),
			formatReportNumber(item.paidByCustomer),
			formatReportNumber(item.paidByCustomerOffPlatform),
			formatReportNumber(item.paidByLender),
			formatReportNumber(item.totalPaid),
			formatReportNumber(item.invoicePrice),
			formatReportNumber(item.oemCollectedShippingFees),
			formatReportNumber(item.grossOemOwed),
			formatVehiclePaymentStatus(item.vehiclePaymentStatus),
			item.paymentNotes || '--',
			item.disputeStatus || '--',
			item.externalStatus || '--',
		]);

		// Combine headers and rows
		const csvContent = [headers, ...rows]
			.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
			.join('\n');

		// Create BOM for UTF-8 (helps Excel open it correctly)
		const BOM = '\uFEFF';
		const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

		// Generate filename with current date
		const now = new Date();
		const month = String(now.getMonth() + 1).padStart(2, '0');
		const day = String(now.getDate()).padStart(2, '0');
		const year = now.getFullYear();
		const filename = `Ekho_Orders_Report_${month}_${day}_${year}.csv`;

		// Create download link and trigger download
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', filename);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	return (
		<Group itemsAlignX='space-between' itemsAlignY='end'>
			<Group itemsSpacing={8} itemsAlignY='center' noWrap>
				<Stack>
					<Text size='footnote' color='secondary'>
						Month
					</Text>
					<Dropdown
						onSelectionChange={(value) => setSelectedMonth(value as string)}
						options={monthOptions}
						className='dropdown'
						value={selectedMonth}
						size='small'
					/>
				</Stack>
				<Stack>
					<Text size='footnote' color='secondary'>
						Year
					</Text>
					<Dropdown
						onSelectionChange={(value) => setSelectedYear(value as string)}
						options={yearOptions}
						className='dropdown'
						value={selectedYear}
						size='small'
					/>
				</Stack>
				<Stack>
					<div style={{ height: '16px' }} />
					<Button
						variant='filled'
						size='small'
						onPress={handleGenerateReport}
						isDisabled={isGenerating}>
						{isGenerating ? 'Generating...' : 'Generate'}
					</Button>
				</Stack>
			</Group>

			<Button
				variant='secondary'
				size='small'
				onPress={handleDownloadReport}
				isDisabled={!hasReportData}>
				Download
			</Button>
		</Group>
	);
}
